import { isMissingDerivedLayerField } from 'components/form/formHelpers'
import { Coordinate } from 'components/form/inputs/coordinatesInput/helpers/coordinateTranslations'
import React, { useEffect, useRef } from 'react'
import styled from 'styled-components'

import { BaseFormField, BaseFormFieldProps } from './BaseFormField'

const FieldFocusWrapper = styled.div`
  display: contents;
`

const FOCUSABLE_FIELD_SELECTOR =
  'input:not([disabled]), textarea:not([disabled]), button:not([disabled]), [role="combobox"]:not([disabled])'

interface ToggleFormFieldProps extends BaseFormFieldProps {
  canEditField: boolean
  canReadField: boolean
  inEditMode: boolean
  inResolveMode?: boolean
  focusOnResolve?: boolean
  highlightCoordinate: (coordinate: Coordinate | null) => void
  displayCoordinateUpdates: (newCoords: ReadonlyArray<Coordinate>) => void
}

export const ToggleFormField: React.FC<ToggleFormFieldProps> = ({
  formItemKey,
  formField,
  fieldValue,
  fieldUnits,
  canEditField,
  units,
  inEditMode,
  inResolveMode,
  focusOnResolve = false,
  formControl,
  formSetValue,
  formErrors,
  showLabel = true,
  ariaLabelledBy,
  formTrigger,
  pointAndClickApi,
  highlightCoordinate,
  displayCoordinateUpdates,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const needsResolving = isMissingDerivedLayerField(formField) && inResolveMode
  const fieldColor = needsResolving ? 'warning' : 'default'
  const fieldMode =
    (inEditMode || fieldColor === 'warning') && canEditField ? 'edit' : 'read'
  const shouldFocus = focusOnResolve && needsResolving

  useEffect(() => {
    if (!shouldFocus) {
      return
    }

    requestAnimationFrame(() => {
      const focusable = containerRef.current?.querySelector<HTMLElement>(
        FOCUSABLE_FIELD_SELECTOR
      )
      focusable?.scrollIntoView({ block: 'nearest' })
      focusable?.focus()
    })
  }, [shouldFocus, formItemKey])

  return (
    <FieldFocusWrapper ref={containerRef}>
      <BaseFormField
        showLabel={showLabel}
        fieldUnits={fieldUnits}
        fieldValue={fieldValue}
        formItemKey={formItemKey}
        formField={formField}
        formControl={formControl}
        formSetValue={formSetValue}
        formErrors={formErrors}
        formTrigger={formTrigger}
        units={units}
        fieldMode={fieldMode}
        fieldColor={fieldColor}
        ariaLabelledBy={ariaLabelledBy}
        pointAndClickApi={pointAndClickApi}
        highlightCoordinate={highlightCoordinate}
        displayCoordinateUpdates={displayCoordinateUpdates}
      />
    </FieldFocusWrapper>
  )
}
